// Stealth obfuscation - replaces every rendered letter/digit on Re:Vault's own
// chrome with a RANDOMLY-CHOSEN Unicode look-alike drawn from a DIVERSE pool:
// Cyrillic / Greek / Armenian confusables mixed with the full set of complete
// Mathematical-Alphanumeric styles (bold, italic, sans, mono, …) and fullwidth
// forms. A fresh choice is made per character, so repeated letters differ and
// no two page loads ever produce the same codepoints. The text still reads
// normally to a human. Proxied iframe content is a separate document and is
// never touched.

const LOWER = "abcdefghijklmnopqrstuvwxyz";
const UPPER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

// Complete Mathematical-Alphanumeric styles (no gaps → no tofu). Bases are the
// codepoint of the first letter ('a'/'A') or digit ('0') in that style.
const MATH_LOWER = [0x1d41a, 0x1d44e, 0x1d482, 0x1d5ba, 0x1d5ee, 0x1d622, 0x1d656, 0x1d68a]; // bold, italic, bold-italic, sans, sans-bold, sans-it, sans-bold-it, mono
const MATH_UPPER = [0x1d400, 0x1d434, 0x1d468, 0x1d5a0, 0x1d5d4, 0x1d608, 0x1d63c, 0x1d670];
const MATH_DIGIT = [0x1d7ce, 0x1d7d8, 0x1d7e2, 0x1d7ec, 0x1d7f6]; // bold, double-struck, sans, sans-bold, mono

// Curated cross-script confusables (all assigned, render in essentially every
// font). Stored as codepoints to avoid any source-encoding pitfalls.
const CROSS = {
	a: [0x430], c: [0x441, 0x3f2], d: [0x501], e: [0x435], g: [0x261], h: [0x4bb], i: [0x456, 0x131],
	j: [0x458], l: [0x4cf], o: [0x43e, 0x3bf, 0x585], p: [0x440, 0x3c1], s: [0x455], u: [0x57d],
	v: [0x3bd, 0x475], w: [0x51d], x: [0x445], y: [0x443, 0x3b3],
	A: [0x410, 0x391], B: [0x412, 0x392], C: [0x421, 0x3f9], E: [0x415, 0x395], H: [0x41d, 0x397],
	I: [0x406, 0x399], J: [0x408], K: [0x41a, 0x39a], M: [0x41c, 0x39c], N: [0x39d],
	O: [0x41e, 0x39f, 0x555], P: [0x420, 0x3a1], S: [0x405], T: [0x422, 0x3a4], X: [0x425, 0x3a7],
	Y: [0x423, 0x3a5], Z: [0x396],
};

// Build the pool: char -> array of look-alike strings.
const POOL = Object.create(null);
const add = (ch, cp) => (POOL[ch] || (POOL[ch] = [])).push(typeof cp === "number" ? String.fromCodePoint(cp) : cp);
for (let i = 0; i < 26; i++) {
	for (const b of MATH_LOWER) add(LOWER[i], b + i);
	for (const b of MATH_UPPER) add(UPPER[i], b + i);
}
// Mathematical Italic small-h has no codepoint (U+1D455 is reserved) → U+210E.
POOL["h"] = POOL["h"].map((c) => (c === String.fromCodePoint(0x1d455) ? "ℎ" : c));
for (let i = 0; i < 10; i++) for (const b of MATH_DIGIT) add(String(i), b + i);
// (Fullwidth forms are intentionally excluded - they render with wide CJK
// spacing, which breaks the "still reads normally" goal.)
for (const ch in CROSS) for (const cp of CROSS[ch]) add(ch, cp);

const pick = (arr) => arr[(Math.random() * arr.length) | 0];

// Random per character - repeated letters and repeated loads all differ.
export function homoglyph(str) {
	let out = "";
	for (const ch of String(str == null ? "" : str)) { const p = POOL[ch]; out += p ? pick(p) : ch; }
	return out;
}

// Stable-per-load variant (caches each input string) so re-rendered surfaces
// like the address bar and tab titles don't shimmer on every paint, while still
// being freshly randomized on each page load.
const stableCache = new Map();
const STABLE_MAX = 500; // FIFO cap so a long session of distinct addr/title strings can't grow it unbounded
export function homoglyphStable(str) {
	const s = String(str == null ? "" : str);
	let v = stableCache.get(s);
	if (v === undefined) {
		v = homoglyph(s);
		if (stableCache.size >= STABLE_MAX) stableCache.delete(stableCache.keys().next().value); // evict oldest
		stableCache.set(s, v);
	}
	return v;
}

const SKIP = { SCRIPT: 1, STYLE: 1, TEXTAREA: 1, INPUT: 1, SELECT: 1, OPTION: 1, NOSCRIPT: 1 };
const originals = new Map(); // textNode -> original value (for clean restore)
let observer = null;
let active = false;

function shouldSkip(node) {
	const p = node.parentNode;
	if (!p || p.nodeType !== 1) return true;
	if (SKIP[p.nodeName]) return true;
	if (p.closest && p.closest("[data-no-obf]")) return true;
	return !node.nodeValue || !node.nodeValue.trim();
}

function transformNode(node) {
	if (shouldSkip(node)) return;
	if (!originals.has(node)) originals.set(node, node.nodeValue);
	node.nodeValue = homoglyph(originals.get(node));
}

function walk(root) {
	const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
	const nodes = [];
	let n;
	while ((n = tw.nextNode())) nodes.push(n);
	nodes.forEach(transformNode);
}

// Drop `originals` entries for a removed subtree so the map doesn't grow unbounded
// across a long session (the SPA constantly adds/removes text nodes while obf is on).
// Removed nodes are gone from the DOM, so there's nothing to restore on disable.
function forget(root) {
	if (!originals.size) return;
	if (root.nodeType === 3) { originals.delete(root); return; }
	if (root.nodeType !== 1) return;
	const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
	let n;
	while ((n = tw.nextNode())) originals.delete(n);
}

export function isObfuscated() { return active; }

export function applyObfuscation(enabled) {
	if (enabled === active) return;
	active = enabled;
	if (enabled) {
		walk(document.body);
		observer = new MutationObserver((muts) => {
			for (const m of muts) {
				if (m.type === "childList") {
					m.addedNodes.forEach((node) => {
						if (node.nodeType === 3) transformNode(node);
						else if (node.nodeType === 1) walk(node);
					});
					m.removedNodes.forEach(forget);
				} else if (m.type === "characterData" && !originals.has(m.target)) {
					transformNode(m.target);
				}
			}
		});
		observer.observe(document.body, { childList: true, subtree: true });
	} else {
		if (observer) { observer.disconnect(); observer = null; }
		for (const [node, orig] of originals) { try { node.nodeValue = orig; } catch (e) {} }
		originals.clear();
		stableCache.clear(); // re-randomize next time it's enabled
	}
}
